import {ProductDataStore} from "../stores/productDataStore";
import {PriceService} from "./priceService";
import {UIService} from "../components/UIService";
import {EventService} from "./eventService";
import {AppStateService} from "./appStateService";
import cloneDeep from "lodash/cloneDeep";

export class ProductDataService {

    private readonly CASEMENT_WINDOW_FUNCTION_ID = 11;
    private productDataStore: ProductDataStore;
    private priceService: PriceService;
    private uiService: UIService;
    private eventService: EventService;
    private appStateService: AppStateService;

    constructor() {
        this.productDataStore = new ProductDataStore();
        this.appStateService = new AppStateService();
        this.priceService = new PriceService();
        this.uiService = new UIService();
        this.eventService = new EventService();
    }

    updateHandle(value)
    {
        this.productDataStore.updateSelectedOption('handle', value);
        this.validateAccessories();
        this.refreshPrice();
    }

    updateSelectedOptionByName(name, id, refreshPrice = false, refreshDrawing = false) {
        this.productDataStore.updateSelectedOption(name, id);
        if (refreshPrice) {
            this.refreshPrice();
        }
        if (refreshDrawing) {
            this.updateProductDrawing();
        }
    }

    refreshPrice(){
        this.priceService.recalculatePrice();
    };

    validateAccessories(){};

    addMullion(direction: 'horizontal'|'vertical'): number
    {
        const attributeName = `${direction}Count`;
        const mullions = this.productDataStore.getAttributeByName('mullions');

        mullions[attributeName] = mullions[attributeName] + 1;

        const mullionLayout = this.generateBasicMullionsLayoutData(mullions['horizontalCount'], mullions['verticalCount']);

        mullions['horizontal'] = mullionLayout['horizontal'];
        mullions['vertical'] = mullionLayout['vertical'];
        this.productDataStore.setAttributeByName('mullions', mullions);
        this.updateProductDrawing();
        this.refreshPrice();

        return mullions[attributeName];
    }

    removeMullion(direction: 'horizontal'|'vertical'): number
    {

        const attributeName = `${direction}Count`;
        const mullions = this.productDataStore.getAttributeByName('mullions');
        const currentMullionsCount = mullions[attributeName];

        if (currentMullionsCount === 0) {
            return 0;
        }
        mullions[attributeName] = mullions[attributeName] - 1;

        const mullionLayout = this.generateBasicMullionsLayoutData(mullions['horizontalCount'], mullions['verticalCount']);

        mullions['horizontal'] = mullionLayout['horizontal'];
        mullions['vertical'] = mullionLayout['vertical'];
        this.productDataStore.setAttributeByName(attributeName, mullions);
        this.updateProductDrawing();
        this.refreshPrice();

        return mullions[attributeName];
    }

    updateMullionPosition(editedMullion, newPosition)
    {
        const direction = editedMullion.direction;
        const mullions = this.productDataStore.getAttributeByName('mullions');

        if (direction === 'horizontal') {
            mullions[direction].forEach(mullionsRow => {
                mullionsRow.forEach(mullion => {
                    if (mullion.id === editedMullion.id) {
                        mullion.customPosition = newPosition;
                        return;
                    }
                })
            })
        } else {
            mullions[direction].forEach(mullion => {
                if (mullion.id === editedMullion.id) {
                    mullion.customPosition = newPosition;
                    return;
                }
            })
        }
        this.updateProductDrawing();
    }

    addMuntin(direction: 'horizontal'|'vertical'): number
    {
        const objectKey = `${direction}Count`;
        const muntins = this.productDataStore.getAttributeByName('muntins');
        muntins[objectKey] = muntins[objectKey] + 1;

        this.productDataStore.setAttributeByName('muntins', muntins);
        this.updateProductDrawing();
        this.refreshPrice();

        return muntins[objectKey];
    }

    removeMuntin(direction: 'horizontal'|'vertical'): number
    {

        const objectKey = `${direction}Count`;
        const muntins = this.productDataStore.getAttributeByName('muntins');
        const currentMuntinsCount = muntins[objectKey];

        if (currentMuntinsCount === 0) {
            return 0;
        }

        muntins[objectKey] = currentMuntinsCount - 1;

        this.productDataStore.setAttributeByName('muntins', muntins);
        this.updateProductDrawing();
        this.refreshPrice();

        return muntins[objectKey];
    }

    resetMullionsAndMuntins()
    {
        this.productDataStore.updateSelectedOption('predefinedMullionLayout', null);
        this.productDataStore.setAttributeByName('dividersHorizontal', 0);
        this.productDataStore.setAttributeByName('dividersVertical', 0);
        this.productDataStore.setAttributeByName('muntins', {
            "isCustomLayout": false,
            "horizontalCount": 0,
            "verticalCount": 0,
            "layout": [],
        });
    }

    updatePredefinedMullionLayout(muntinLayout)
    {
        this.productDataStore.updateSelectedOption('predefinedMullionLayout', muntinLayout.id);
        this.productDataStore.setAttributeByName('muntins', cloneDeep(muntinLayout.data.muntins));
        this.productDataStore.setAttributeByName('mullions',  cloneDeep(muntinLayout.data.mullions));

        const horizontalMullionInput: any = this.uiService.getElementByAttribute('nf-template-action', 'horizontalMullionInput');
        const verticalMullionInput: any = this.uiService.getElementByAttribute('nf-template-action', 'verticalMullionInput');
        horizontalMullionInput.value = muntinLayout.data.mullions.horizontalCount;
        verticalMullionInput.value = muntinLayout.data.mullions.verticalCount;

        const horizontalMuntinInput: any = this.uiService.getElementByAttribute('nf-template-action', 'horizontalMuntinInput');
        const verticalMuntinInput: any = this.uiService.getElementByAttribute('nf-template-action', 'verticalMuntinInput');
        horizontalMuntinInput.value = muntinLayout.data.muntins.horizontalCount ?? 0;
        verticalMuntinInput.value = muntinLayout.data.muntins.verticalCount ?? 0;

        this.updateProductDrawing();
        this.refreshPrice();

    }

    updateProductWidth(width)
    {
        this.productDataStore.setAttributeByName('width', parseInt(width));
    }

    updateProductHeight(height)
    {
        this.productDataStore.setAttributeByName('height', parseInt(height));
    }

    updateProductBottomPanel(height)
    {
        this.productDataStore.setAttributeByName('bottomPanel', parseInt(height));
        this.updateProductDrawing();
        this.refreshPrice();
    }

    updateProductDrawing()
    {
        if (this.isEntranceDoor()) {
            this.eventService.emit('reloadDoorDrawing');
        }

        if (this.appStateService.getAppSettingOption('iccDrawing')) {
            this.eventService.emit("reloadDrawing");
        }
    }

    isRoofWindow()
    {
        return this.productDataStore.getProductType() === 'roof_window';
    }

    isEntranceDoor() {
        return this.productDataStore.getProductType() === 'door';
    }

    isCircleProduct(): boolean
    {
        return this.productDataStore.getProductShape() === 'circle';
    }

    isTriangleProduct(): boolean
    {
        return this.productDataStore.getProductShape() === 'triangle';
    }

    canBeTurnedIntoCasementWindow()
    {
        return this.productDataStore.getAttributeByName('canBeTurnedIntoCasementWindow');
    }

    currentFunctionIsCasementWindow()
    {
        return this.productDataStore.getAttributeByName('windowFunctionId') === this.CASEMENT_WINDOW_FUNCTION_ID;
    }

    hasFlashingOptions(): boolean
    {
        return this.productDataStore.hasAvailableOptionsByName('flashing');
    }

    getMaximumProductHeight(): number
    {
        return this.productDataStore.getAttributeByName('maxHeight');
    }

    getMinimumProductHeight(): number
    {
        return this.productDataStore.getAttributeByName('minHeight');
    }

    getMinimumProductWidth(): number
    {
        return this.productDataStore.getAttributeByName('minWidth');
    }

    getMaximumProductWidth(): number
    {
        const maxWidth = this.productDataStore.getAttributeByName('maxWidth');
        return parseInt(maxWidth);
    }

    getMaximumProductWidthForColor(): number
    {
        const maxDimensionsRules = this.getMaxDimensionForColorRules();
        return parseInt(maxDimensionsRules.width);
    }

    getMaximumProductHeightForColor(): number
    {
        const maxDimensionsRules = this.getMaxDimensionForColorRules();
        return parseInt(maxDimensionsRules.height);
    }

    canHaveBottomPanel(): boolean
    {
        return !!this.productDataStore.getAttributeByName('canHaveBottomPanel');
    }

    canHaveHingeSideOption(): boolean
    {
        return !!this.productDataStore.getAttributeByName('canHaveHingeSideOption');
    }

    public reloadProductWithNewSerie(serieId, overrideProductSlug = null) {
        // const itemId = this.apiService.itemId;
        const currentSerieId = this.productDataStore.getSelectedOptionIdByName('series');

        if (currentSerieId === serieId) {
            return;
        }

        const serie = this.productDataStore.getAvailableOptionByNameAndId('series', serieId);
        const serieSlug = serie.attributes.slug.toLowerCase();
        const type = this.productDataStore.getProductType().toLowerCase();
        const material = this.productDataStore.getProductInfoAttributeByName('productMaterial');
        const productSlug = (overrideProductSlug) ? overrideProductSlug : this.productDataStore.getProductInfoAttributeByName('productSlug');
        const path = `${window.location.protocol}//${window.location.host}`;
        let route = `${path}/${type}/${material}/${serieSlug}/${productSlug}`;

        const hingeOptionSelector: any = this.uiService.getElementByAttribute('nf-template-input', 'productHingeSideOption');
        localStorage.setItem('shape', JSON.stringify({
            height: Math.floor(this.productDataStore.getAttributeByName('height')),
            width: Math.floor(this.productDataStore.getAttributeByName('width')),
            bottomPanel: this.productDataStore.getAttributeByName('bottomPanel'),
            unit: this.productDataStore.getSelectedOptionIdByName('unit'),
            hingeSide: this.productDataStore.getSelectedOptionIdByName(
                this.uiService.getAttributeFromElement(hingeOptionSelector, 'nf-option-name')
            ),
        }));
        localStorage.setItem('relationship', JSON.stringify(
            this.productDataStore.getAllSelectedOptions()
        ));
        window.location.href = route;
    }

    getMaxDimensionForColorRules() {
        const maxDimensionRules = this.productDataStore.getData().rules.max_dimension;
        if (Object.keys(maxDimensionRules).length > 0) {
            const currentColor = this.productDataStore.getSelectedOptionData('outer');

            if (currentColor.attributes.isWhite) {
                return maxDimensionRules.white;
            } else {
                return maxDimensionRules.color;
            }
        }

        // for the case where we don't have any rules for colors we return just impossible value
        // so all products will match the "if" statement later
        //we have to remember that we have to return object with keys width/height
        return {
            width: 999999999,
            height: 999999999
        };
    }

    getCurrentMainSashWidth()
    {
        const sashConfiguration = this.productDataStore.getSashConfiguration();

        return this.productDataStore.getAttributeByName('width') / sashConfiguration.standardFields;
    }

    getPartDiscountBannerByModule(moduleName): string
    {
        return this.productDataStore.getProductInfoAttributeByName('partDiscountsImages')[moduleName];
    }

    isPredefinedMullionsLayoutSelected()
    {
        return this.productDataStore.getSelectedOptionData('predefinedMullionLayout') !== null;
    }

    removePredefinedMullionsLayout()
    {
        this.updateSelectedOptionByName('predefinedMullionLayout', null);
        this.productDataStore.setAttributeByName('muntins', {
            "isCustomLayout": false,
            "horizontalCount": 0,
            "verticalCount": 0,
            "layout": [],
        });
    }

    generateBasicMullionsLayoutData(horizontalMullions: number = 0, verticalMullions: number = 0): object
    {
        let mullionId = 1;
        let verticalMullionsLayout = [];
        let horizontalMullionsLayout = [];
        let verticalMullionOffset = 1 / (verticalMullions + 1);
        let verticalMullionPosition = 0;
        let horizontalMullionOffset = 1 / (horizontalMullions + 1);
        let horizontalMullionPosition = 0;
        const positionPresision = 6;

        for (let i = 1; i <= verticalMullions; i++) {
            verticalMullionPosition = i * verticalMullionOffset;
            verticalMullionsLayout.push({
                id: mullionId,
                position: parseFloat(verticalMullionPosition.toFixed(positionPresision)),
            });

            mullionId++;
        }

        for (let rowIndex = 0; rowIndex < horizontalMullions; rowIndex++) {
            horizontalMullionsLayout[rowIndex] = [];
            for (let j = 1; j <= verticalMullions + 1; j++) {
                horizontalMullionPosition = (rowIndex + 1) * horizontalMullionOffset;
                horizontalMullionsLayout[rowIndex].push({
                    id: mullionId,
                    position: parseFloat(horizontalMullionPosition.toFixed(positionPresision)),
                });
                mullionId++;
            }
        }

        return {
            horizontal: horizontalMullionsLayout,
            vertical: verticalMullionsLayout,
        };
    }

    applyCartMullionLayout(selectedLayoutId)
    {
        const layoutData = this.productDataStore.getAvailableOptionsByName('mullionLayoutsInCart').filter(layout => {
            return layout.id === selectedLayoutId;
        });
        this.productDataStore.setAttributeByName('mullions', layoutData[0].mullions);
        this.productDataStore.setAttributeByName('muntins', layoutData[0].muntins);
        if (layoutData[0].predefinedMullionLayout) {
            this.productDataStore.updateSelectedOption('predefinedMullionLayout', layoutData[0].predefinedMullionLayout);
        }

        const horizontalMullionInput: any = this.uiService.getElementByAttribute('nf-template-action', 'horizontalMullionInput');
        const verticalMullionInput: any = this.uiService.getElementByAttribute('nf-template-action', 'verticalMullionInput');
        horizontalMullionInput.value = layoutData[0].mullions.horizontalCount;
        verticalMullionInput.value = layoutData[0].mullions.verticalCount;

        const horizontalMuntinInput: any = this.uiService.getElementByAttribute('nf-template-action', 'horizontalMuntinInput');
        const verticalMuntinInput: any = this.uiService.getElementByAttribute('nf-template-action', 'verticalMuntinInput');
        horizontalMuntinInput.value = layoutData[0].muntins.horizontalCount ?? 0;
        verticalMuntinInput.value = layoutData[0].muntins.verticalCount ?? 0;

        this.updateProductDrawing();
        this.refreshPrice();
    }

    mirrorTriangleShape()
    {
        const shape = this.productDataStore.getProductShapeConfiguration();
        const s1 = shape.s1;
        const s3 = shape.s3;
        shape.s1 = s3;
        shape.s3 = s1;
    }
}
